// Serviceability helpers shared by the create-order location step.
//
// A city is "serviceable" when it exists in the public active-cities catalog
// (matched case-insensitively by name). Precedence for the service-area check
// (mirrors the backend; the backend stays the source of truth):
//   1. If the city has a `service_polygon` with >= 3 points, the order coords
//      must fall INSIDE the polygon (ray-casting point-in-polygon).
//   2. Else if center_lat + center_lng + radius_km are set, the order coords
//      must fall within radius_km of the center (Haversine distance).
//   3. Else an active city-name match alone is sufficient.

export const NOT_SERVICEABLE_MESSAGE = 'Сорі, поки не працюємо у цьому місті';

const EARTH_RADIUS_KM = 6371;

const toRad = (value) => (value * Math.PI) / 180;

// Great-circle distance between two lat/lng points in kilometers.
export const haversineKm = (lat1, lng1, lat2, lng2) => {
    const dLat = toRad(lat2 - lat1);
    const dLng = toRad(lng2 - lng1);

    const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) *
        Math.sin(dLng / 2) * Math.sin(dLng / 2);

    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return EARTH_RADIUS_KM * c;
};

// Normalize a stored polygon into a list of {lat, lng} numeric points,
// skipping any malformed entries.
const normalizePolygon = (polygon) => {
    if (!Array.isArray(polygon)) {
        return [];
    }

    return polygon.reduce((points, point) => {
        if (!point || typeof point !== 'object') {
            return points;
        }
        const lat = toNumber(point.lat);
        const lng = toNumber(point.lng);
        if (lat === null || lng === null) {
            return points;
        }
        points.push({lat, lng});
        return points;
    }, []);
};

// Ray-casting point-in-polygon test. `polygon` is an ORDERED list of
// {lat, lng} points (>= 3); it is treated as closed (the last point connects
// back to the first). Treats lat as the y-axis and lng as the x-axis.
export const pointInPolygon = (lat, lng, polygon) => {
    const points = normalizePolygon(polygon);
    if (points.length < 3) {
        return false;
    }

    const y = toNumber(lat);
    const x = toNumber(lng);
    if (y === null || x === null) {
        return false;
    }

    let inside = false;
    const n = points.length;
    let j = n - 1;
    for (let i = 0; i < n; i += 1) {
        const yi = points[i].lat;
        const xi = points[i].lng;
        const yj = points[j].lat;
        const xj = points[j].lng;

        const intersects =
            (yi > y) !== (yj > y) &&
            x < ((xj - xi) * (y - yi)) / ((yj - yi) || 1e-12) + xi;

        if (intersects) {
            inside = !inside;
        }
        j = i;
    }

    return inside;
};

const normalize = (value) => (value || '').toString().trim().toLowerCase();

const toNumber = (value) =>
    value === null || value === undefined || value === '' ? null : Number(value);

// Returns { ok: boolean, error: string|null }.
// activeCities is the list returned by GET /cities/ (already active only).
export const checkServiceability = (cityName, latitude, longitude, activeCities) => {
    const cities = Array.isArray(activeCities) ? activeCities : [];

    // Nothing chosen yet — not serviceable (blocks "Next"), but don't surface
    // the scary "we don't work in your city" banner until the user has actually
    // picked an address/city.
    if (!cityName) {
        return {ok: false, error: null};
    }

    const match = cities.find(
        (city) => city && city.is_active !== false && normalize(city.name) === normalize(cityName)
    );

    if (!match) {
        return {ok: false, error: NOT_SERVICEABLE_MESSAGE};
    }

    const polygon = normalizePolygon(match.service_polygon);
    const hasPolygon = polygon.length >= 3;

    const centerLat = toNumber(match.center_lat);
    const centerLng = toNumber(match.center_lng);
    const radiusKm = toNumber(match.radius_km);
    const hasRange = centerLat !== null && centerLng !== null && radiusKm !== null;

    const lat = toNumber(latitude);
    const lng = toNumber(longitude);

    // (1) Polygon takes precedence: coords must be INSIDE the polygon.
    if (hasPolygon) {
        if (lat === null || lng === null) {
            return {ok: false, error: NOT_SERVICEABLE_MESSAGE};
        }
        if (!pointInPolygon(lat, lng, polygon)) {
            return {ok: false, error: NOT_SERVICEABLE_MESSAGE};
        }
    } else if (hasRange) {
        // (2) Center + radius fallback: coords must be within radius_km.
        if (lat === null || lng === null) {
            return {ok: false, error: NOT_SERVICEABLE_MESSAGE};
        }

        const distance = haversineKm(centerLat, centerLng, lat, lng);

        if (distance > radiusKm) {
            return {ok: false, error: NOT_SERVICEABLE_MESSAGE};
        }
    }

    // (3) Else: active city-name match alone is sufficient.
    return {ok: true, error: null};
};
